/**
 * i18n Configuration
 *
 * This configuration supports dynamic language loading:
 * - Add any new language file to /public/locales/{lang}.json
 * - No code changes required to support new languages
 * - Languages are detected from: URL params (?lng=fr), localStorage, browser settings
 * - Falls back to English if translation key is missing
 *
 * Usage:
 * - Switch language: i18n.changeLanguage('fr')
 * - Get current language: i18n.language
 * - Use in components: const { t } = useTranslation();
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import bundledEnglishTranslations from './assets/en.json';
export const FALLBACK_LANGUAGE = 'en';
export const I18N_NAMESPACE = 'translation';
export const hasTranslationResources = (language) => i18n.hasResourceBundle(language, I18N_NAMESPACE);
export const ensureBundledEnglishFallback = () => {
    if (!hasTranslationResources(FALLBACK_LANGUAGE)) {
        i18n.addResourceBundle(FALLBACK_LANGUAGE, I18N_NAMESPACE, bundledEnglishTranslations, true, true);
    }
};
i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
    // Remove hardcoded language - let i18next detect from browser/URL/localStorage
    fallbackLng: FALLBACK_LANGUAGE,
    partialBundledLanguages: true,
    resources: {
        [FALLBACK_LANGUAGE]: {
            [I18N_NAMESPACE]: bundledEnglishTranslations,
        },
    },
    // Remove supportedLngs to allow any language file to be loaded dynamically
    // This allows adding new language files without code changes
    backend: {
        loadPath: '/locales/{{lng}}.json',
        // Add error handling for missing language files
        allowMultiLoading: false,
        // If a language file fails to load, fallback to English
        requestOptions: {
            cache: 'default',
        },
    },
    // Language detection configuration
    detection: {
        // Try to detect language from various sources
        order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
        lookupQuerystring: 'lng',
        lookupLocalStorage: 'i18nextLng',
        caches: ['localStorage'],
    },
    interpolation: {
        escapeValue: false,
    },
    // Enhanced fallback behavior
    ns: [I18N_NAMESPACE],
    defaultNS: I18N_NAMESPACE,
    // If a key is missing, fallback to English
    saveMissing: false,
    // Load failure handling
    load: 'all',
    // Debugging (can be enabled in development)
    debug: process.env.NODE_ENV === 'development',
    // React specific options
    react: {
        useSuspense: true, // Enable suspense for proper loading state handling
    },
})
    .then(() => {
    ensureBundledEnglishFallback();
    console.log('i18n initialization complete');
    console.log('Current language:', i18n.language);
    console.log('Available resources:', Object.keys(i18n.store.data));
})
    .catch((error) => {
    console.error('i18n initialization failed:', error);
});
// Export a promise that resolves when i18n is ready
export const i18nReady = new Promise((resolve) => {
    if (i18n.isInitialized) {
        resolve();
    }
    else {
        i18n.on('initialized', () => {
            resolve();
        });
    }
});
export default i18n;
